import { useMemo } from 'react';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip,
    Legend, ResponsiveContainer,
} from 'recharts';
import MatrixData from '../../../../graphData/MatrixData';
import { CATEGORICAL, CHART_MARK_STROKE, CHART_MARK_STROKE_WIDTH } from '../../../../../helpers/chartPalette';

// AI-CONTEXT: See docs/patterns.md#data-template-shadow-render
/**
 * Renders a chart for a single QuestionnaireField in template mode, deriving the
 * data entirely from the pre-fetched `templateData.effects.{indicators, responses,
 * matrixRows, measureMoments}` slice. No Firestore subscriptions.
 *
 * Mirrors what `IndicatorData → DataType → <leaf>` does in live mode, but with a
 * smaller dependency surface — leaves and `DataType` are NOT touched.
 *
 * @typedef {object} TemplateIndicatorChartProps
 * @property {string} fieldId - QuestionnaireField.ID
 * @property {object} templateData - Full response from exportTemplateData
 * @property {string} [graphType]  'bar' | 'line' | 'pie' (default 'bar')
 * @property {Array<string>} [colorPalette]
 * @property {Object<string, string>} [colorOverrides] - Per-indicator colour map; `primary` colours the total-growth metric.
 * @property {string} [numberFontSize] - CSS font-size for the total-growth number view.
 * @property {'left'|'center'|'right'} [numberTextAlign] - Alignment for the total-growth number view.
 * @property {string} [numberFontWeight] - Font weight for the total-growth number view.
 *
 * @param {TemplateIndicatorChartProps} props
 */
const TemplateIndicatorChart = ({ fieldId, templateData, graphType = 'bar', graphColor, colorPalette, colorOverrides, numberFontSize, numberTextAlign, numberFontWeight }) => {

    const slice = templateData?.data?.effects;
    const indicator = useMemo(
        () => (slice?.indicators || []).find(i => i.ID === fieldId),
        [slice, fieldId]
    );

    const fieldResponses = useMemo(
        () => (slice?.responses || []).filter(r => r.FieldID === fieldId),
        [slice, fieldId]
    );

    const measureMoments = useMemo(() => {
        if (!indicator) return [];
        // Find moments belonging to the same research as this indicator's questionnaire.
        const research = (templateData?.data?.research || []).find(
            r => r.QuestionnaireID === indicator.QuestionnaireID
        );
        if (!research) return [];
        return (slice?.measureMoments || [])
            .filter(m => m.ResearchID === research.ID)
            .slice()
            .sort((a, b) => (a.Position ?? 0) - (b.Position ?? 0));
    }, [slice, templateData, indicator]);

    if (!indicator) {
        return <p>Voorbeeldvraag niet gevonden in template data.</p>;
    }

    // Sjabloonweergave staat buiten een live rapportage van een echte
    // organisatie, dus hier geldt het vaste palet — geen eigen kopie meer.
    const palette = colorPalette || CATEGORICAL;

    switch (indicator.Type) {
        case 'multiple-one':
        case 'multiple-multiple':
            return (
                <MultipleChart
                    fieldResponses={fieldResponses}
                    measureMoments={measureMoments}
                    palette={palette}
                    graphColor={graphColor}
                />
            );

        case 'scale':
            return (
                <NumericChart
                    fieldResponses={fieldResponses}
                    measureMoments={measureMoments}
                    palette={palette}
                    graphColor={graphColor}
                    label="Gemiddelde score"
                />
            );

        case 'number':
            return (
                <NumericChart
                    fieldResponses={fieldResponses}
                    measureMoments={measureMoments}
                    palette={palette}
                    graphColor={graphColor}
                    label="Gemiddelde waarde"
                />
            );

        case 'paragraph':
            return <ParagraphList fieldResponses={fieldResponses} />;

        case 'matrix-one':
        case 'matrix-multiple':
            return (
                <MatrixData
                    field={fieldId}
                    researchId={indicator.QuestionnaireID
                        ? (templateData?.data?.research || []).find(r => r.QuestionnaireID === indicator.QuestionnaireID)?.ID
                        : null}
                    graphType={graphType}
                    graphColor={graphColor}
                    colorPalette={palette}
                    colorOverrides={colorOverrides}
                    numberFontSize={numberFontSize}
                    numberTextAlign={numberTextAlign}
                    numberFontWeight={numberFontWeight}
                    templateData={templateData}
                />
            );

        default:
            return <p>Vraagtype "{indicator.Type}" wordt nog niet ondersteund in voorbeeldweergave.</p>;
    }
};

/**
 * Bar chart of response counts per chosen option, grouped by measure moment.
 *
 * @param {object} props
 * @param {Array<object>} props.fieldResponses
 * @param {Array<object>} props.measureMoments
 * @param {Array<string>} props.palette
 */
const MultipleChart = ({ fieldResponses, measureMoments, palette, graphColor }) => {

    const { data, optionKeys } = useMemo(() => {
        const optionSet = new Set();
        const byMoment = new Map();
        fieldResponses.forEach(r => {
            const opt = r.OptionType === 'other' ? 'Anders' : (r.Input || 'Onbekend');
            optionSet.add(opt);
            const momentKey = r.MomentID || 'onbekend';
            if (!byMoment.has(momentKey)) byMoment.set(momentKey, {});
            const bucket = byMoment.get(momentKey);
            bucket[opt] = (bucket[opt] || 0) + 1;
        });

        const moments = measureMoments.length
            ? measureMoments.map(m => ({ id: m.ID, label: m.Title || `Meetmoment ${m.Position ?? ''}`.trim() }))
            : Array.from(byMoment.keys()).map(id => ({ id, label: id === 'onbekend' ? 'Onbekend' : id }));

        const data = moments
            .map(({ id, label }) => ({ name: label, ...(byMoment.get(id) || {}) }))
            .filter(row => Object.keys(row).length > 1);

        return { data, optionKeys: Array.from(optionSet) };
    }, [fieldResponses, measureMoments]);

    if (!data.length) return <p>Geen voorbeelddata beschikbaar.</p>;

    const axisColor = graphColor || 'black';

    return (
        <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data}>
                <XAxis dataKey="name" axisLine={false} tickLine={false} stroke={axisColor} tick={{ fill: axisColor }} />
                <YAxis allowDecimals={false} axisLine={false} tickLine={false} stroke={axisColor} tick={{ fill: axisColor }} />
                <RechartsTooltip />
                <Legend wrapperStyle={{ color: axisColor }} />
                {optionKeys.map((key, idx) => (
                    <Bar key={key} dataKey={key} fill={palette[idx % palette.length]} stroke={CHART_MARK_STROKE} strokeWidth={CHART_MARK_STROKE_WIDTH} />
                ))}
            </BarChart>
        </ResponsiveContainer>
    );
};

/**
 * Bar chart of average numeric value per measure moment. Used for `scale` and `number`.
 *
 * @param {object} props
 * @param {Array<object>} props.fieldResponses
 * @param {Array<object>} props.measureMoments
 * @param {Array<string>} props.palette
 * @param {string} props.label - Tooltip label and bar series name
 */
const NumericChart = ({ fieldResponses, measureMoments, palette, label, graphColor }) => {

    const data = useMemo(() => {
        const sums = new Map();
        fieldResponses.forEach(r => {
            const value = Number(r.Input ?? r.Value ?? r.Result ?? NaN);
            if (Number.isNaN(value)) return;
            const key = r.MomentID || 'onbekend';
            const bucket = sums.get(key) || { total: 0, count: 0 };
            bucket.total += value;
            bucket.count += 1;
            sums.set(key, bucket);
        });

        const moments = measureMoments.length
            ? measureMoments.map(m => ({ id: m.ID, label: m.Title || `Meetmoment ${m.Position ?? ''}`.trim() }))
            : Array.from(sums.keys()).map(id => ({ id, label: id === 'onbekend' ? 'Onbekend' : id }));

        return moments
            .map(({ id, label }) => {
                const bucket = sums.get(id);
                if (!bucket || bucket.count === 0) return null;
                return { name: label, value: Number((bucket.total / bucket.count).toFixed(2)), count: bucket.count };
            })
            .filter(Boolean);
    }, [fieldResponses, measureMoments]);

    if (!data.length) return <p>Geen voorbeelddata beschikbaar.</p>;

    const axisColor = graphColor || 'black';

    return (
        <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data}>
                <XAxis dataKey="name" axisLine={false} tickLine={false} stroke={axisColor} tick={{ fill: axisColor }} />
                <YAxis axisLine={false} tickLine={false} stroke={axisColor} tick={{ fill: axisColor }} />
                <RechartsTooltip formatter={(v) => [v, label]} />
                <Bar dataKey="value" name={label} fill={palette[0]} />
            </BarChart>
        </ResponsiveContainer>
    );
};

/**
 * Simple list of text excerpts for paragraph-type questions.
 *
 * @param {object} props
 * @param {Array<object>} props.fieldResponses
 */
const ParagraphList = ({ fieldResponses }) => {
    const excerpts = fieldResponses
        .filter(r => r.Input)
        .slice(0, 5);

    if (!excerpts.length) return <p>Geen voorbeeldantwoorden beschikbaar.</p>;

    return (
        <ul className="template-paragraph-list">
            {excerpts.map((r) => (
                <li key={r.ID || r.docid}>
                    "{r.Input}"
                </li>
            ))}
        </ul>
    );
};

/**
 * Stacked bar chart per matrix row (rows = sub-questions, bars = chosen option counts).
 *
 * @param {object} props
 * @param {string} props.fieldId
 * @param {Array<object>} props.fieldResponses
 * @param {Array<object>} props.matrixRows
 * @param {Array<string>} props.palette
 */
const MatrixChart = ({ fieldId, fieldResponses, matrixRows, palette }) => {

    const rows = useMemo(
        () => matrixRows.filter(r => r.Field === fieldId),
        [matrixRows, fieldId]
    );

    const { data, optionKeys } = useMemo(() => {
        const optionSet = new Set();
        const byRow = new Map();
        const rowTitleByRowId = new Map();

        fieldResponses.forEach(r => {
            const rowKey = r.RowID || 'unknown';
            const opt = r.Input || 'Onbekend';
            optionSet.add(opt);
            if (!byRow.has(rowKey)) byRow.set(rowKey, {});
            const bucket = byRow.get(rowKey);
            bucket[opt] = (bucket[opt] || 0) + 1;
            if (r.RowTitle && !rowTitleByRowId.has(rowKey)) {
                rowTitleByRowId.set(rowKey, r.RowTitle);
            }
        });

        const labelFor = (rowId) =>
            rows.find(r => r.ID === rowId)?.Title
            || rowTitleByRowId.get(rowId)
            || rowId;

        const data = rows.length
            ? rows.map(row => ({ name: row.Title || row.ID, ...(byRow.get(row.ID) || {}) }))
            : Array.from(byRow.entries()).map(([k, v]) => ({ name: labelFor(k), ...v }));

        return {
            data: data.filter(row => Object.keys(row).length > 1),
            optionKeys: Array.from(optionSet),
        };
    }, [fieldResponses, rows]);

    if (!data.length) return <p>Geen voorbeelddata beschikbaar.</p>;

    return (
        <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data} layout="vertical">
                <XAxis type="number" allowDecimals={false} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" width={150} axisLine={false} tickLine={false} />
                <RechartsTooltip />
                <Legend />
                {optionKeys.map((key, idx) => (
                    <Bar key={key} dataKey={key} stackId="a" fill={palette[idx % palette.length]} />
                ))}
            </BarChart>
        </ResponsiveContainer>
    );
};

export default TemplateIndicatorChart;
